// Contact form settings: one form for all five tabs (FormSettings.tsx), so switching tabs keeps unsaved edits. Preview only.

export type AnswerType = 'short' | 'yesno' | 'choose' | 'tick';
export type ShowOn = 'Both forms' | 'Contact form' | 'Sign-up form';

export interface FormQuestion {
    id: string;
    label: string;
    // second line; left out = "Required" / "Optional"
    detail?: string;
    required: boolean;
    // full name, email, subject and message: always on the form and always required
    locked?: boolean;
    shown: boolean;
    type?: AnswerType;
    choices?: string[];
    showOn?: ShowOn;
}

export type SpamLayer = 'filter' | 'links' | 'confirm' | 'honeypot' | 'timeTrap' | 'rateLimit' | 'challenge' | 'hold';

export interface ContactFormSettings {
    recipients: string[];
    bell: boolean;
    dailySummary: boolean;
    autoReply: boolean;
    autoReplyMessage: string;
    questions: FormQuestion[];
    optIn: boolean;
    consentWording: string;
    privacyLine: string;
    thankYou: boolean;
    thankYouMessage: string;
    redirect: boolean;
    redirectUrl: string;
    spam: Record<SpamLayer, boolean>;
    blockedWords: string;
    blockedDomains: string;
}

export interface TabProps {
    form: ContactFormSettings;
    setForm: (update: (f: ContactFormSettings) => ContactFormSettings) => void;
}

export const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// spec 713:5478, in the spec's order
export const SPAM_LAYERS: { key: SpamLayer; label: string; hint: string }[] = [
    { key: 'filter', label: 'Investi spam filter', hint: 'Blocks bots without annoying puzzles' },
    { key: 'links', label: 'Block links from unknown senders', hint: 'Recommended' },
    { key: 'confirm', label: 'Require email confirmation', hint: 'Sender clicks a link before you see it' },
    { key: 'honeypot', label: 'Hidden honeypot field', hint: 'Catches simple bots. Invisible to real visitors.' },
    { key: 'timeTrap', label: 'Time trap', hint: 'Rejects a form filled in under two seconds.' },
    { key: 'rateLimit', label: 'Limit messages per sender', hint: 'No more than 3 an hour from the same person.' },
    { key: 'challenge', label: 'Challenge when a message looks risky', hint: 'A quick tick-box, only for suspicious senders.' },
    { key: 'hold', label: 'Hold blocked messages for review', hint: 'Keeps them for 30 days instead of deleting.' },
];

// sample settings (specs 410:12432, 713:6606, 713:6230, 713:5854, 713:5478)
export const initialSettings = (host: string, short: string): ContactFormSettings => ({
    recipients: [`info@${host}`, `tom@${host}`],
    bell: true,
    dailySummary: false,
    autoReply: true,
    autoReplyMessage: `Thank you for contacting ${short}. We've received your message and one of our team will reply within two business days.\n\n`
        + 'For urgent share registry matters (holding statements, change of address) please contact Automic Group on 1300 288 664.\n\n'
        + `Kind regards,\nThe ${short} team`,
    questions: [
        { id: 'name', label: 'Full name', required: true, locked: true, shown: true },
        { id: 'email', label: 'Email address', required: true, locked: true, shown: true },
        { id: 'phone', label: 'Phone number', required: false, shown: true },
        { id: 'shareholder', label: 'Are you a current shareholder?', detail: 'Yes / No — helps Investi match them to the registry', required: false, shown: true },
        { id: 'subject', label: 'Subject', detail: 'Dropdown: General · Investor enquiry · Media · Supplier', required: true, locked: true, shown: true },
        { id: 'message', label: 'Message', required: true, locked: true, shown: true },
        { id: 'company', label: 'Company / organisation', required: false, shown: true },
    ],
    optIn: true,
    consentWording: 'Also send me announcements by email. You can unsubscribe at any time.',
    privacyLine: 'By sending this message you agree to our Privacy Policy.',
    thankYou: true,
    thankYouMessage: 'Thanks — your message is with us. We usually reply within one business day.',
    redirect: false,
    redirectUrl: '/contact-us/thank-you/',
    spam: { filter: true, links: true, confirm: false, honeypot: true, timeTrap: true, rateLimit: true, challenge: false, hold: true },
    blockedWords: 'crypto, seo services, guest post',
    blockedDomains: '@mailinator.com, @spam4.me',
});
